// The co-op VISIT screens (docs/COOP-INVITES-PLAN.md), mounted once for the whole site:
//   • HOST card      — shown on any page while someone is visiting your kingdom: the live coffer, their wage, the next count, [Count now] [Send away]
//   • GUEST card     — shown in the host's kingdom (the estate): the coffer, your wage, [WORK], today's work, [RETURN HOME]
//   • GUEST banner   — you have an open stay but are not in the host's kingdom: [Go there] [End stay]
//   • toasts         — truthful notices (counted coffer, guest left, sent away …)
// All numbers come from the server through game/coopManager.js; nothing here invents a figure.

import { postMessage } from './messageRail.js'
import './invites.css'
import './coop.css'
import { Events, on, emit } from './eventBus.js'
import { getCoop, work, countNow, oust, beginVisit, leaveStay, heist, seize } from '../game/coopManager.js'
import { formatCoins } from './format.js'
import { countdown } from './invitePopup.js'
import { CoopConfig } from '../config/balance.js'
import { setHtmlIfChanged, addCloseButton } from './domUtil.js'
import { openProposeDialog } from './proposeDialog.js'

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
const regNo = (n) => (n == null ? '—' : String(n).padStart(6, '0'))
const left = (t) => (t == null ? 'until sent away' : countdown(t - Date.now()))
const secs = (ms) => Math.max(0, Math.ceil(ms / 1000))
const MIN_KEY = 'mao-kingdom/coop/hostMin/v1'
const readMin = () => { try { return sessionStorage.getItem(MIN_KEY) === '1' } catch { return false } }

export function mountCoopPanel() {
  const layer = document.createElement('div')
  layer.className = 'coop-layer'
  document.body.appendChild(layer)
  const host = document.createElement('section'); host.className = 'coop-card coop-host'; host.hidden = true; host.setAttribute('aria-label', 'Guests in your kingdom')
  const guest = document.createElement('section'); guest.className = 'coop-card coop-guest'; guest.hidden = true; guest.setAttribute('aria-label', 'Your visit')
  const banner = document.createElement('div'); banner.className = 'coop-banner'; banner.hidden = true
  const alertEl = document.createElement('section'); alertEl.className = 'coop-alert'; alertEl.hidden = true; alertEl.setAttribute('role', 'alert')
  const toasts = document.createElement('div'); toasts.className = 'coop-toasts'; toasts.setAttribute('role', 'status'); toasts.setAttribute('aria-live', 'polite')
  layer.append(host, guest, banner, alertEl, toasts)
  let busyWork = false

  function paintHost(c) {
    host.hidden = !c.host.length
    if (!c.host.length) return
    const min = readMin()
    host.classList.toggle('is-min', min)
    setHtmlIfChanged(host, `
      <header><span class="coop-kicker">Guests in your kingdom</span><button type="button" class="coop-min" data-coop="min" aria-label="${min ? 'Show' : 'Hide'} the guests card">${min ? '+' : '–'}</button></header>
      ${min ? `<p class="coop-sum">${c.host.length} guest${c.host.length === 1 ? '' : 's'} · coffer ${esc(formatCoins(c.host.reduce((a, s) => a + s.coffer, 0)))}</p>` : `
      <p class="coop-note">Everything you earn now goes into the coffer — counted every ${CoopConfig.countHours} h and when they leave.</p>
      ${c.host.map((s) => `<div class="coop-row">
        <div class="coop-who"><b>${esc(s.guest.name)}</b> <small>No. ${regNo(s.guest.registryNo)} · ${esc(s.guest.kingdom ?? '')}</small></div>
        <dl class="coop-dl">
          <div><dt>Coffer</dt><dd data-coffer>${esc(formatCoins(s.coffer))}</dd></div>
          <div><dt>Their wage</dt><dd>${s.wagePct}%${s.coffer ? ` (${esc(formatCoins(Math.floor(s.coffer * s.wagePct / 100)))} now)` : ''}</dd></div>
          <div><dt>Next count</dt><dd>${esc(left(s.nextCountAt))}</dd></div>
          <div><dt>Their stay ends</dt><dd>${esc(left(s.endsAt))}</dd></div>
        </dl>
        ${s.pursuit?.status === 'running' ? '<p class="coop-run">RUNNING OFF WITH THE COFFER — see the alert</p>' : `<div class="coop-act"><button type="button" class="coop-btn" data-coop="count" data-id="${esc(s.id)}">Count now</button><button type="button" class="coop-btn coop-btn--danger" data-coop="oust" data-id="${esc(s.id)}" data-name="${esc(s.guest.name)}">Send away</button>${s.wed?.can ? `<button type="button" class="coop-btn coop-btn--quiet" data-coop="propose" data-who="${esc(s.guestId)}" data-name="${esc(s.guest.name)}" title="Propose marriage to ${esc(s.guest.name)}">♥ Propose</button>` : ''}</div>`}
      </div>`).join('')}`}`)
  }

  function paintGuest(c) {
    const s = c.guest
    const show = !!s && c.inEstate
    guest.hidden = !show
    if (!show) return
    const wage = Math.floor(s.coffer * s.wagePct / 100)
    const run = s.pursuit?.status === 'running' ? s.pursuit : null
    const minHeist = CoopConfig.heistMinCoffer
    if (run) {
      setHtmlIfChanged(guest, `
        <header><span class="coop-kicker coop-kicker--run">You are running</span></header>
        <h2>With ${esc(formatCoins(run.coffer))}</h2>
        <p class="coop-count" data-run-left></p>
        <p class="coop-small">Hold out until the time is up and the coffer is yours. If ${esc(s.host.name)} catches you first, the court fines you ${esc(formatCoins(run.stolen))} — taken from what you earned, never from coin you bought — and you may not visit anyone for ${CoopConfig.banDays} days.</p>`)
      return
    }
    setHtmlIfChanged(guest, `
      <header><span class="coop-kicker">You are a guest</span></header>
      <h2>${esc(s.host.name)}'s kingdom</h2>
      <p class="coop-from">No. ${regNo(s.host.registryNo)} · ${esc(s.host.kingdom ?? '')}</p>
      <dl class="coop-dl">
        <div><dt>Coffer</dt><dd data-coffer>${esc(formatCoins(s.coffer))}</dd></div>
        <div><dt>Your wage</dt><dd>${s.wagePct}%${s.coffer ? ` (${esc(formatCoins(wage))} so far)` : ''}</dd></div>
        <div><dt>Next count</dt><dd>${esc(left(s.nextCountAt))}</dd></div>
        <div><dt>Your stay ends</dt><dd>${esc(left(s.endsAt))}</dd></div>
      </dl>
      <button type="button" class="coop-work" data-coop="work" ${busyWork ? 'disabled' : ''}>Work <span>+${esc(formatCoins(s.workYield ?? 0))}</span></button>
      <p class="coop-small">Work today: ${s.workToday ?? 0} / ${(s.workCap ?? 0).toLocaleString()} · the coffer is counted every ${CoopConfig.countHours} h; you are paid your wage then and when you leave.</p>
      <p class="coop-small">Your own kingdom keeps running while you are here — goons can still attack your home.</p>
      <div class="coop-act"><button type="button" class="coop-btn" data-coop="home">Return home</button><button type="button" class="coop-btn coop-btn--quiet coop-btn--danger" data-coop="run" ${s.coffer >= minHeist ? '' : 'disabled'} title="${s.coffer >= minHeist ? 'Try to run off with the coffer' : `There must be at least ${minHeist}m in the coffer`}">Run with the coffer…</button></div>
      ${s.wed?.can ? `<p class="coop-small coop-wed"><button type="button" class="coop-link" data-coop="propose" data-who="${esc(s.hostId)}" data-name="${esc(s.host.name)}">♥ Propose marriage to ${esc(s.host.name)}</button></p>` : ''}`)
  }

  function paintAlert(c) {
    const running = c.host.filter((s) => s.pursuit?.status === 'running')
    alertEl.hidden = !running.length
    if (!running.length) return
    setHtmlIfChanged(alertEl, running.map((s) => {
      return `<div class="coop-alert-row">
        <div class="coop-alert-kicker">Thief!</div>
        <h2>${esc(s.guest.name)} is running off with the coffer</h2>
        <p><b>${esc(formatCoins(s.pursuit.coffer))}</b> is in their hands. Catch them before the time runs out and the whole coffer comes back to you — and the court fines them.</p>
        <p class="coop-count" data-alert-left="${esc(s.id)}"></p>
        <button type="button" class="coop-seize" data-coop="seize" data-id="${esc(s.id)}">SEIZE</button>
      </div>`
    }).join(''))
  }

  function paintBanner(c) {
    const s = c.guest
    banner.hidden = !(s && !c.inEstate)
    if (banner.hidden) return
    setHtmlIfChanged(banner, `<span>You are a guest of <b>${esc(s.host.name)}</b> — coffer ${esc(formatCoins(s.coffer))}.</span> <button type="button" class="coop-btn" data-coop="go">Go there</button> <button type="button" class="coop-btn coop-btn--quiet" data-coop="end">End stay</button>`)
  }

  function paint(c = getCoop()) { paintHost(c); paintGuest(c); paintBanner(c); paintAlert(c) }

  function toast(text, kind = 'ok') { // (a co-op notice: a message on the rail)
    postMessage({ kind: 'info', type: `COOP_${String(kind).toUpperCase()}`, icon: kind === 'ok' ? '🤝' : '⚠', title: 'Co-op', text, lifeMs: 9000 })
  }

  layer.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-coop]'); if (!b) return
    switch (b.dataset.coop) {
      case 'min': try { sessionStorage.setItem(MIN_KEY, readMin() ? '0' : '1') } catch { /* fine */ } paint(); break
      case 'count': countNow(b.dataset.id); break
      case 'seize': seize(b.dataset.id); break
      case 'run': { const sg = getCoop().guest; if (sg && confirm(`Run off with the coffer?\n\nYou must hold out ${CoopConfig.heistWindowMs / 1000} seconds. If ${sg.host.name} catches you first, the court fines you what you tried to steal (${formatCoins(sg.coffer - Math.floor(sg.coffer * sg.wagePct / 100))}), takes it from what you earned, and bars you from visiting for ${CoopConfig.banDays} days. If not, the whole coffer is yours.`)) heist(); break }
      case 'oust': if (confirm(`Send ${b.dataset.name} home? The coffer is counted at once and they are paid their wage.`)) oust(b.dataset.id); break
      case 'work': if (!busyWork) { busyWork = true; b.disabled = true; await work(); setTimeout(() => { busyWork = false; paint() }, CoopConfig.workCooldownMs) } break
      case 'home': emit(Events.LeaveLand, {}); break
      case 'propose': openProposeDialog({ id: b.dataset.who, name: b.dataset.name, onDone: (msg) => emit(Events.CoopNotice, { text: msg, kind: 'ok' }) }); break
      case 'go': beginVisit(); break
      case 'end': if (confirm('End your stay? The coffer is counted and you are paid your wage.')) leaveStay(); break
      default:
    }
  })
  document.addEventListener('keydown', (e) => { // Space = Work while you are in the host's kingdom (not while typing)
    if (e.code !== 'Space' || guest.hidden || /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(document.activeElement?.tagName ?? '')) return
    e.preventDefault(); layer.querySelector('[data-coop="work"]')?.click()
  })

  on(Events.CoopChange, (c) => paint(c))
  on(Events.CoopNotice, ({ text, kind }) => toast(text, kind))
  setInterval(() => { if (!host.hidden || !guest.hidden || !banner.hidden) paint() }, 1000) // countdowns
  setInterval(() => { // the pursuit clock ticks smoothly (just the number)
    const c = getCoop()
    for (const el of layer.querySelectorAll('[data-alert-left]')) { const s = c.host.find((x) => x.id === el.dataset.alertLeft); if (s?.pursuit) el.textContent = `${secs(s.pursuit.deadline + (c.skew ?? 0) - Date.now())} s` }
    const rl = layer.querySelector('[data-run-left]'); const g = c.guest?.pursuit
    if (rl && g) rl.textContent = `${secs(g.deadline + (c.skew ?? 0) - Date.now())} s`
  }, 250)
  paint()
}
